/**
 * Currency Context
 *
 * Provides user's display currency preference throughout the application.
 * All backend amounts are stored and returned in VND.
 * This context manages the user's preferred display currency for UI.
 *
 * Features:
 * - Fetch user's display currency preference on mount
 * - Provide context to all components
 * - Handle currency updates
 * - Cache exchange rates
 *
 * Usage:
 * ```tsx
 * const { displayCurrency, setDisplayCurrency, exchangeRate } = useDisplayCurrency();
 * ```
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '@/services/user.service';
import type { CurrencyCode } from '@/lib/currency';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';

/**
 * Exchange rate data structure
 */
export interface ExchangeRate {
  fromCurrency: CurrencyCode;
  toCurrency: CurrencyCode;
  rate: number;
  lastUpdated: string;
}

/**
 * Currency Context value
 */
interface CurrencyContextValue {
  /**
   * User's preferred display currency
   * Default: VND (matches backend storage currency)
   */
  displayCurrency: CurrencyCode;

  /**
   * Update user's display currency preference
   */
  setDisplayCurrency: (currency: CurrencyCode) => Promise<void>;

  /**
   * Current exchange rate from VND to display currency
   * Rate = 1 means no conversion (same currency)
   */
  exchangeRate: number;

  /**
   * Loading state for currency operations
   */
  isLoading: boolean;

  /**
   * Get exchange rate for a specific currency pair
   */
  getExchangeRate: (from: CurrencyCode, to: CurrencyCode) => number;
}

/**
 * Currency Context
 */
const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

/**
 * Currency Provider Props
 */
interface CurrencyProviderProps {
  children: ReactNode;
}

/**
 * Currency Provider Component
 *
 * Wraps the application to provide currency context
 */
export function CurrencyProvider({ children }: CurrencyProviderProps) {
  const queryClient = useQueryClient();
  const [displayCurrency, setDisplayCurrencyState] = useState<CurrencyCode>('VND');
  const [exchangeRate, setExchangeRate] = useState<number>(1);

  // Get authentication state - only fetch currency when user is authenticated
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Fetch user's display currency preference
  const { data: userCurrency, isLoading } = useQuery({
    queryKey: ['user', 'displayCurrency'],
    queryFn: async () => {
      try {
        const currency = await userService.getDisplayCurrency();
        return currency;
      } catch (error) {
        // If API not yet implemented or error, default to VND
        console.warn('Failed to fetch display currency, using VND:', error);
        return 'VND' as CurrencyCode;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry if endpoint doesn't exist yet
    // Only fetch currency if user is authenticated (prevents 401 on OAuth callback)
    enabled: isAuthenticated,
  });

  // Fetch exchange rate when display currency changes
  const { data: exchangeRateData } = useQuery({
    queryKey: ['exchangeRate', 'VND', displayCurrency],
    queryFn: async () => {
      // If same currency, no conversion needed
      if (displayCurrency === 'VND') {
        return { rate: 1, lastUpdated: new Date().toISOString() };
      }

      try {
        // TODO: Replace with actual API call when backend endpoint is ready
        // const rate = await userService.getExchangeRate('VND', displayCurrency);

        // Mock exchange rates for now (TEMPORARY - WAIT FOR BACKEND)
        const mockRates: Record<string, number> = {
          USD: 0.000041, // 1 VND = 0.000041 USD (approx 24,400 VND/USD)
          EUR: 0.000038, // 1 VND = 0.000038 EUR
          GBP: 0.000032, // 1 VND = 0.000032 GBP
          JPY: 0.0060,   // 1 VND = 0.006 JPY
          SGD: 0.000054, // 1 VND = 0.000054 SGD
        };

        return {
          rate: mockRates[displayCurrency] || 1,
          lastUpdated: new Date().toISOString(),
        };
      } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
        return { rate: 1, lastUpdated: new Date().toISOString() };
      }
    },
    enabled: !!displayCurrency,
    staleTime: 1000 * 60 * 30, // 30 minutes - exchange rates don't change that often
  });

  // Update display currency mutation
  const updateCurrencyMutation = useMutation({
    mutationFn: (currency: CurrencyCode) => userService.updateDisplayCurrency(currency),
    onSuccess: (_, currency) => {
      setDisplayCurrencyState(currency);
      queryClient.invalidateQueries({ queryKey: ['user', 'displayCurrency'] });
      queryClient.invalidateQueries({ queryKey: ['exchangeRate'] });
      toast.success(`Display currency updated to ${currency}`);
    },
    onError: (error) => {
      console.error('Failed to update display currency:', error);
      toast.error('Failed to update currency preference');
    },
  });

  // Sync state with fetched user preference
  useEffect(() => {
    if (userCurrency && userCurrency !== displayCurrency) {
      setDisplayCurrencyState(userCurrency);
    }
  }, [userCurrency, displayCurrency]);

  // Update exchange rate when data changes
  useEffect(() => {
    if (exchangeRateData) {
      setExchangeRate(exchangeRateData.rate);
    }
  }, [exchangeRateData]);

  /**
   * Set display currency and persist to backend
   */
  const setDisplayCurrency = async (currency: CurrencyCode): Promise<void> => {
    return updateCurrencyMutation.mutateAsync(currency);
  };

  /**
   * Get exchange rate for a specific currency pair
   * For now, only supports conversion from VND
   */
  const getExchangeRate = (from: CurrencyCode, to: CurrencyCode): number => {
    if (from === to) return 1;

    // Currently only support VND as base currency
    if (from !== 'VND') {
      console.warn(`Exchange rate not supported for ${from} to ${to}, returning 1`);
      return 1;
    }

    // If converting from VND to current display currency, use cached rate
    if (to === displayCurrency) {
      return exchangeRate;
    }

    // Otherwise, mock rates (TEMPORARY - WAIT FOR BACKEND)
    const mockRates: Record<string, number> = {
      VND: 1,
      USD: 0.000041,
      EUR: 0.000038,
      GBP: 0.000032,
      JPY: 0.0060,
      SGD: 0.000054,
    };

    return mockRates[to] || 1;
  };

  const value: CurrencyContextValue = {
    displayCurrency,
    setDisplayCurrency,
    exchangeRate,
    isLoading,
    getExchangeRate,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

/**
 * Hook to use currency context
 *
 * @throws Error if used outside CurrencyProvider
 *
 * @example
 * ```tsx
 * const { displayCurrency, exchangeRate } = useDisplayCurrency();
 * const displayAmount = amountVND * exchangeRate;
 * ```
 */
export function useDisplayCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);

  if (context === undefined) {
    throw new Error('useDisplayCurrency must be used within a CurrencyProvider');
  }

  return context;
}
